import { DevicePlatform, EnrollmentStatus, Role } from '../enums';
import { PermissionGroup, PermissionService } from '../permissionService/PermissionService';
import { VdbsService } from '../vdbs/VdbsService';
import { CommissionIdConverter, ICommissionData } from './CommissionIdConverter';
import { NotEnrolledVehicleError } from './errors/NotEnrolledVehicleError';
import { IJSONPatchOperation, IVumUserVehicleRelation, IVumUserVehicleRelationsResponse, VumClient } from './VumClient';
import {
    IVumFlattenedVehicle,
    IVumFlattenedVehicleForUpdate,
    VumFlattenedVehicleConverter,
} from './VumFlattenedVehicleConverter';

export class VumService {
    constructor(
        private readonly vumClient: VumClient,
        private readonly vdbsService: VdbsService,
        private readonly permissionService: PermissionService,
    ) {}

    public async getVehicles(): Promise<IVumFlattenedVehicle[]> {
        const vumResponse = await this.vumClient.getUserVehicleRelations();
        let vehicles: IVumFlattenedVehicle[] = [];
        if (vumResponse.relations) {
            const enrichedVehicles = await this.loadDevicePlatformIfMissing(vumResponse);
            vehicles = VumFlattenedVehicleConverter.convertUserVehicleRelationsToFlattenedVehicles(enrichedVehicles);
        }
        return vehicles;
    }

    public async getVehicle(vin: string): Promise<IVumFlattenedVehicle | undefined> {
        const vumResponse = await this.vumClient.getUserVehicleRelation(vin);
        let relation: IVumUserVehicleRelation = vumResponse.relation;
        if (!relation) {
            return undefined;
        }
        if (!relation.vehicle?.modBackend) {
            const modBackend = await this.vdbsService.getDevicePlatform(vin);
            relation = { ...relation, vehicle: { ...relation.vehicle, modBackend } };
        }
        return VumFlattenedVehicleConverter.convertUserVehicleRelationsToFlattenedVehicles([relation])[0];
    }

    public async updateNicknameAndLicensePlateForVehicle(vehicle: IVumFlattenedVehicleForUpdate): Promise<void> {
        const dataToUpdate: IJSONPatchOperation[] = [
            {
                op: 'replace',
                path: '/vehicleNickname',
                value: vehicle.nickname ?? '',
            },
            {
                op: 'replace',
                path: '/licensePlate',
                value: vehicle.licensePlateNumber ?? '',
            },
        ];
        if (vehicle.vin) {
            await this.vumClient.patchVinVehicle(vehicle.vin, dataToUpdate);
        } else {
            const commissionData: ICommissionData = {
                commissionNumber: vehicle.commissionNumber!,
                bid: vehicle.bid!,
                commissionNumberYear: vehicle.commissionNumberYear!,
            };
            await this.vumClient.patchCommissionVehicle(commissionData, dataToUpdate);
        }
    }

    public async updatePrimaryVehicle(vehicle: IVumFlattenedVehicleForUpdate, isPrimaryCar: boolean): Promise<void> {
        const dataToUpdate: IJSONPatchOperation[] = [
            {
                op: 'replace',
                path: '/primaryCar',
                value: String(isPrimaryCar),
            },
        ];
        if (vehicle.vin) {
            await this.vumClient.patchVinVehicle(vehicle.vin, dataToUpdate);
        } else {
            const commissionData: ICommissionData = {
                commissionNumber: vehicle.commissionNumber!,
                bid: vehicle.bid!,
                commissionNumberYear: vehicle.commissionNumberYear!,
            };
            await this.vumClient.patchCommissionVehicle(commissionData, dataToUpdate);
        }
    }

    public async addVinToCommissionDataVehicle(vehicle: IVumFlattenedVehicleForUpdate, vin: string): Promise<void> {
        const commissionId = CommissionIdConverter.convertCommissionDataToId(
            vehicle.commissionNumber,
            vehicle.bid,
            vehicle.commissionNumberYear,
        );
        await this.vumClient.addVinToUserVehicle(commissionId!, vin);
    }

    public async addVehicle(vehicle: IVumFlattenedVehicleForUpdate): Promise<IVumFlattenedVehicle> {
        if (this.hasVehicleIdentifier(vehicle)) {
            await this.vumClient.addUserVehicleRelation(
                VumFlattenedVehicleConverter.convertFlattenedVehicleToUserVehicleRelation(vehicle),
            );
            return this.getAddedVehicleOrFallback(vehicle);
        } else {
            throw Error('Invalid vehicle');
        }
    }

    public async deleteVehicle(vehicle: IVumFlattenedVehicleForUpdate): Promise<void> {
        if (vehicle.vin || (vehicle.commissionNumber && vehicle.bid && vehicle.commissionNumberYear)) {
            await this.vumClient.deleteUserVehicleRelation(
                VumFlattenedVehicleConverter.convertFlattenedVehicleToUserVehicleRelation(vehicle),
            );
        } else {
            throw Error('Invalid vehicle');
        }
    }

    public async disenrollUser(vehicle: IVumFlattenedVehicle): Promise<void> {
        if (!vehicle.vin) {
            throw new NotEnrolledVehicleError();
        }
        const { PU_COMPLETED, GU, SU } = PermissionGroup;
        if (this.permissionService.hasPermission(vehicle, PU_COMPLETED)) {
            await this.vumClient.resetPrimaryUser(vehicle.vin);
        } else if (this.permissionService.hasPermission(vehicle, SU, GU)) {
            await this.vumClient.resetNonPrimaryUser(vehicle.vin);
        } else {
            throw new NotEnrolledVehicleError();
        }
    }

    public async getMbbUserId(): Promise<string> {
        const vumResponse = await this.vumClient.getUserVehicleRelations();
        return vumResponse?.user?.mbbUserId ?? '';
    }

    public async deleteUser(): Promise<void> {
        await this.vumClient.deleteUser();
    }

    public async removeUserFromVehicle(userId: string, vin: string): Promise<void> {
        await this.vumClient.removeUserFromVehicle(userId, vin);
    }

    private async loadDevicePlatformIfMissing(
        vumResponse: IVumUserVehicleRelationsResponse,
    ): Promise<IVumUserVehicleRelation[]> {
        return Promise.all(
            vumResponse.relations.map(async (relation) => {
                const modBackend =
                    relation.vehicle?.modBackend ?? (await this.vdbsService.getDevicePlatform(relation.vehicle?.vin));
                return {
                    ...relation,
                    vehicle: { ...relation.vehicle, modBackend },
                } as IVumUserVehicleRelation;
            }),
        );
    }

    private hasVehicleIdentifier(vehicle: IVumFlattenedVehicleForUpdate) {
        return vehicle.vin || (vehicle.commissionNumber && vehicle.bid && vehicle.commissionNumberYear);
    }

    private async getAddedVehicleOrFallback(vehicle: IVumFlattenedVehicleForUpdate): Promise<IVumFlattenedVehicle> {
        const fallbackVehicle: IVumFlattenedVehicle = {
            ...vehicle,
            role: Role.UNKNOWN,
            enrollmentStatus: EnrollmentStatus.UNKNOWN,
            devicePlatform: DevicePlatform.UNKNOWN,
            primaryCar: false,
        };
        try {
            if (vehicle.vin) {
                return (await this.getVehicle(vehicle.vin)) ?? fallbackVehicle;
            } else {
                return (await this.findMatchingCommissionDataVehicle(vehicle)) ?? fallbackVehicle;
            }
        } catch (e) {
            return fallbackVehicle;
        }
    }

    private async findMatchingCommissionDataVehicle(
        addedVehicle: IVumFlattenedVehicleForUpdate,
    ): Promise<IVumFlattenedVehicle | undefined> {
        const allVehicles = await this.getVehicles();

        return allVehicles.find(
            (vehicle) =>
                vehicle.commissionNumber === addedVehicle.commissionNumber &&
                vehicle.commissionNumberYear === addedVehicle.commissionNumberYear &&
                vehicle.bid === addedVehicle.bid,
        );
    }

    public async patchVinVehicle(vin: string, payload: IJSONPatchOperation[]) {
        return await this.vumClient.patchVinVehicle(vin, payload);
    }
}